import { DateTime } from "luxon";

import {
  defaultEquipmentIndicators,
  defaultEquipmentIndicatorsApartment,
  defaultEquipmentIndicatorsHouse,
  defaultEquipmentIndicatorsMultifamilyHouse,
  defaultModernizationsMatrix,
} from "@/components/refineRealEstate/dataRefineTabs/defaultModernizationEquipmentMatrix";
import {
  ActualUseEnum,
  BasementConversionEnum,
  BasementEnum,
  BuildingStandardDeterminationMethodEnum,
  BuildingTypeEnum,
  Construction,
  HouseOrApartmentTypeEnum,
  ModernizationMethodEnum,
  ObjectTypeEnum,
  OutdoorFacilitiesEnum,
  OwnershipEnum,
  ProductTypeEnum,
  RentReferenceEnum,
  RoofEnum,
} from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

export const initialTabsState: Omit<PropertyInformation, "propertyImages"> = {
  generalInfo: {
    productType: ProductTypeEnum.lendingValue,
    objectType: ObjectTypeEnum.house,
    ownership: OwnershipEnum.fullOwnership,
    valuationDate: DateTime.now(),
    selfUseSuitability: true,
    actualUse: ActualUseEnum.ownerOccupied,
    completionStatus: "completedFull",
  },
  plotData: {
    plotArea: undefined,
    completionStatus: "notStarted",
  },
  buildingData: {
    constructionYear: undefined,
    livingArea: undefined,
    garages: undefined,
    completionStatus: "notStarted",
  },
  energyAndPhotovoltaic: {
    energyCertificate: undefined,
    energyEfficiencyHouseStandard: undefined,
    issueDate: undefined,
    certificateType: undefined,
    finalEnergy: undefined,
    primaryEnergy: undefined,
    CO2Emissions: undefined,
    photovoltaicAvailable: null,
    constructionYear: undefined,
    annualPowerYield: undefined,
    completionStatus: "notStarted",
  },
  buildingStandard: {
    method: BuildingStandardDeterminationMethodEnum.detailed,
    completionStatus: "completedFull",
  },
  modernization: {
    method: ModernizationMethodEnum.detailed,
    matrix: defaultModernizationsMatrix,
    completionStatus: "completedFull",
  },
  financialAndRentalInfo: {
    rent: {
      value: undefined,
      reference: RentReferenceEnum.euroPerSquareMeter,
    },
    garageRent: {
      value: undefined,
      reference: RentReferenceEnum.euroPerSquareMeter,
    },
    parkingSpaceRent: {
      value: undefined,
      reference: RentReferenceEnum.euroPerSquareMeter,
    },
    completionStatus: "notStarted",
  },
};

export const initialTabsStateWithImages: PropertyInformation = {
  ...initialTabsState,
  propertyImages: {
    completionStatus: "notStarted",
  },
};

export const initialStateHouse: PropertyInformation = {
  ...initialTabsStateWithImages,
  generalInfo: {
    ...initialTabsStateWithImages.generalInfo,
    buildingType: BuildingTypeEnum.singleFamilyHouse,
  },
  buildingData: {
    ...initialTabsStateWithImages.buildingData,
    houseOrApartmentType: HouseOrApartmentTypeEnum.detachedHouse,
    numberOfFloors: 1,
    construction: Construction.massive,
    roof: RoofEnum.full,
    basement: BasementEnum.full,
    basementConversion: BasementConversionEnum.none,
    outdoorFacilities: OutdoorFacilitiesEnum.average,
  },
  buildingStandard: {
    ...initialTabsStateWithImages.buildingStandard,
    matrix: {
      ...defaultEquipmentIndicators,
      ...defaultEquipmentIndicatorsHouse,
    },
  },
};

export const initialStateApartment: PropertyInformation = {
  ...initialTabsStateWithImages,
  generalInfo: {
    ...initialTabsStateWithImages.generalInfo,
    buildingType: BuildingTypeEnum.apartment,
  },
  buildingData: {
    ...initialTabsStateWithImages.buildingData,
    outdoorParkingSpaces: undefined,
  },
  buildingStandard: {
    ...initialTabsStateWithImages.buildingStandard,
    matrix: {
      ...defaultEquipmentIndicators,
      ...defaultEquipmentIndicatorsApartment,
    },
  },
};

export const initialStateMultifamilyHouse: PropertyInformation = {
  ...initialTabsStateWithImages,
  generalInfo: {
    ...initialTabsStateWithImages.generalInfo,
    buildingType: BuildingTypeEnum.multiFamilyHouse,
  },
  buildingData: {
    ...initialTabsStateWithImages.buildingData,
    numberOfFloors: 1,
    construction: Construction.massive,
    roof: RoofEnum.full,
    basement: BasementEnum.full,
    basementConversion: BasementConversionEnum.none,
    outdoorParkingSpaces: undefined,
  },
  buildingStandard: {
    ...initialTabsStateWithImages.buildingStandard,
    matrix: {
      ...defaultEquipmentIndicators,
      ...defaultEquipmentIndicatorsMultifamilyHouse,
    },
  },
};
